// src/context/EventContext.jsx
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

// Adjust this import path to wherever you keep these API functions
import {
  getAllEvents,
  getEventById,
  getEventGuests,
  getEventSpeakers,
  getEventHosts,
  getEventGlobalCompassMembers,
  getEventIncludedItems,
  bookEvent,
  likeandUnlikeEvent,
  getMyEvents,
  getFoodPreferenceQuestions,
  submitFoodPreference,
} from "@/Services/EventServices";

const EventContext = createContext(null);

const getErrMsg = (e) =>
  e?.response?.data?.message ||
  e?.response?.data?.detail ||
  e?.response?.data?.error ||
  e?.message ||
  "Something went wrong";

export function EventProvider({ children }) {
  const [events, setEvents] = useState([]); // list page
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsError, setEventsError] = useState("");

  // cache per event id
  const [eventById, setEventById] = useState({}); // { [id]: event }
  const [eventDetailsById, setEventDetailsById] = useState({}); // { [id]: { guests, speakers, ... } }

  const fetchAllEvents = useCallback(async () => {
    setEventsLoading(true);
    setEventsError("");
    try {
      const res = await getAllEvents();
      // normalize (your API might return {status, data:[...]} or directly array)
      const list = Array.isArray(res) ? res : res?.data || [];
      setEvents(Array.isArray(list) ? list : []);
      return { ok: true, data: res };
    } catch (e) {
      const msg = getErrMsg(e);
      setEventsError(msg);
      return { ok: false, error: msg };
    } finally {
      setEventsLoading(false);
    }
  }, []);

  const fetchEventById = useCallback(
    async (id, { cache = true } = {}) => {
      if (!id) return { ok: false, error: "Missing event id" };

      if (cache && eventById[id]) {
        return { ok: true, data: eventById[id], cached: true };
      }

      try {
        const res = await getEventById(id);
        const evt = res?.data ?? res; // normalize
        setEventById((prev) => ({ ...prev, [id]: evt }));
        return { ok: true, data: evt };
      } catch (e) {
        return { ok: false, error: getErrMsg(e) };
      }
    },
    [eventById],
  );

  // Fetch event + related sections in parallel
  const fetchEventDetails = useCallback(async (id) => {
    if (!id) return { ok: false, error: "Missing event id" };

    try {
      const [eventRes, guestsRes, speakersRes, hostsRes, gcRes, includedRes] =
        await Promise.all([
          getEventById(id),
          getEventGuests(id),
          getEventSpeakers(id),
          getEventHosts(id),
          getEventGlobalCompassMembers(id),
          getEventIncludedItems(id),
        ]);

      const evt = eventRes?.data ?? eventRes;

      const details = {
        guests: guestsRes?.data ?? guestsRes ?? [],
        speakers: speakersRes?.data ?? speakersRes ?? [],
        hosts: hostsRes?.data ?? hostsRes ?? [],
        globalCompassMembers: gcRes?.data ?? gcRes ?? [],
        includedItems: includedRes?.data ?? includedRes ?? [],
      };

      setEventById((prev) => ({ ...prev, [id]: evt }));
      setEventDetailsById((prev) => ({ ...prev, [id]: details }));

      return { ok: true, data: { event: evt, ...details } };
    } catch (e) {
      return { ok: false, error: getErrMsg(e) };
    }
  }, []);

  const doBookEvent = useCallback(async (payload) => {
    try {
      const res = await bookEvent(payload);
      return { ok: true, data: res };
    } catch (e) {
      return { ok: false, error: getErrMsg(e) };
    }
  }, []);

  const doLikeUnlike = useCallback(async (payload) => {
    try {
      const res = await likeandUnlikeEvent(payload);
      return { ok: true, data: res };
    } catch (e) {
      return { ok: false, error: getErrMsg(e) };
    }
  }, []);

  const fetchMyEvents = useCallback(async () => {
    try {
      const res = await getMyEvents();
      return { ok: true, data: res };
    } catch (e) {
      return { ok: false, error: getErrMsg(e) };
    }
  }, []);

  const fetchFoodPreferenceQuestions = useCallback(async (eventId) => {
    try {
      const res = await getFoodPreferenceQuestions(eventId);
      return { ok: true, data: res };
    } catch (e) {
      return { ok: false, error: getErrMsg(e) };
    }
  }, []);

  const doSubmitFoodPreference = useCallback(async (payload) => {
    try {
      const res = await submitFoodPreference(payload);
      return { ok: true, data: res };
    } catch (e) {
      return { ok: false, error: getErrMsg(e) };
    }
  }, []);

  const value = useMemo(
    () => ({
      // state
      events,
      eventsLoading,
      eventsError,
      eventById,
      eventDetailsById,

      // actions
      fetchAllEvents,
      fetchEventById,
      fetchEventDetails,
      bookEvent: doBookEvent,
      likeandUnlikeEvent: doLikeUnlike,
      getMyEvents: fetchMyEvents,
      getFoodPreferenceQuestions: fetchFoodPreferenceQuestions,
      submitFoodPreference: doSubmitFoodPreference,
    }),
    [
      events,
      eventsLoading,
      eventsError,
      eventById,
      eventDetailsById,
      fetchAllEvents,
      fetchEventById,
      fetchEventDetails,
      doBookEvent,
      doLikeUnlike,
      fetchMyEvents,
      fetchFoodPreferenceQuestions,
      doSubmitFoodPreference,
    ],
  );

  return (
    <EventContext.Provider value={value}>{children}</EventContext.Provider>
  );
}

export const useEvent = () => useContext(EventContext);
